import React, { useState, useEffect } from 'react';
import { Input, Alert, Table, Select, Switch, Icon } from 'antd';
import PropTypes from 'prop-types';
import {
	getAiInstructions,
	updateAiInstruction,
	createAiInstruction,
	deleteAiInstruction,
} from '../../../utils/api.js';
import { ConfigModal } from '../../ConfigModal.jsx';

const { Column } = Table;
const { Option } = Select;

function AiConfigInstructions({ displayCreateNew, onCreateNew, onCancel }) {
	const [errors, setErrors] = useState([]);
	const [instructions, setInstructions] = useState([]);
	const [openModal, setOpenModal] = useState(false);
	const [selectedInstruction, setSelectedInstruction] = useState({});

	const handleError = (message, description, level = 'error') => {
		setErrors([
			{
				id: new Date().getTime(),
				message,
				description,
				level,
			},
			...errors,
		]);
	};

	const handleCancel = () => {
		setSelectedInstruction({});
		setOpenModal(false);
		onCancel();
	};

	const handleSubmit = async (formValues) => {
		try {
			let result = null;

			if (selectedInstruction && selectedInstruction.id !== null) {
				result = await updateAiInstruction(
					selectedInstruction.id,
					formValues.name,
					formValues.instruction,
					formValues.type,
					formValues.enabled
				);
			} else {
				result = await createAiInstruction(
					formValues.name,
					formValues.instruction,
					formValues.type,
					formValues.enabled
				);

				if (onCreateNew) {
					onCreateNew();
				}
			}

			if (result) {
				fetchData();
				setOpenModal(false);
			}
		} catch (error) {
			handleError('Något gick fel', error.message);
		}
	};

	const handleDelete = async () => {
		try {
			const result = await deleteAiInstruction(selectedInstruction.id);
			if (result) {
				fetchData();
				setOpenModal(false);
			}
		} catch (error) {
			handleError('Något gick fel', error.message);
		}
	};

	const fetchData = async () => {
		const result = await getAiInstructions();
		if (result) {
			setInstructions(result.reverse());
		}
	};

	useEffect(() => {
		try {
			fetchData();
		} catch (error) {
			console.error(error);
		}
	}, []);

	useEffect(() => {
		setSelectedInstruction({});
		setOpenModal(displayCreateNew);
	}, [displayCreateNew]);

	const onRowClick = (obj) => ({
		onClick: () => {
			setSelectedInstruction(obj);
			setOpenModal(true);
		},
	});

	return (
		<div>
			{errors.map((error) => (
				<Alert
					key={error.id}
					message={error.message}
					description={error.description || null}
					type={error.level || 'error'}
					style={{ marginTop: 20 }}
					showIcon
					closable
					onClose={() => setErrors(errors.filter((err) => error.id !== err.id))}
				/>
			))}
			<Table
				dataSource={instructions}
				size='small'
				pagination={{ pageSize: 12, position: 'bottom' }}
				onRow={onRowClick}
				rowKey={(record) => record.id}
			>
				<Column title='Namn' dataIndex='name' key='fullName' width={200} sorter={(a, b) => (a.name.toUpperCase() < b.name.toUpperCase()) ? -1 : (a.name.toUpperCase() > b.name.toUpperCase()) ? 1 : 0} />
				<Column title='Instruktion' dataIndex='instruction' key='instruction' sorter={(a, b) => (a.instruction.toUpperCase() < b.instruction.toUpperCase()) ? -1 : (a.instruction.toUpperCase() > b.instruction.toUpperCase()) ? 1 : 0} />
				<Column
					title='Typ'
					dataIndex='type'
					key='type'
					width={140}
					align='center'
					render={(type) => (type === 'SUMMARY' ? 'Summering' : '-')}
				/>
				<Column
					title='Aktiv'
					dataIndex='enabled'
					key='enabled'
					render={(enabled) =>
						enabled ? <Icon type='check-circle' theme='twoTone' twoToneColor='#52c41a' /> : '-'
					}
					sorter={(a, b) => (a.enabled < b.enabled) ? -1 : (a.enabled > b.enabled) ? 1 : 0}
					align='center'
					width={100}
				/>
			</Table>
			<ConfigModal
				title={`${
					selectedInstruction && selectedInstruction.id ? 'Redigera' : 'Skapa ny'
				} instruktion`}
				open={openModal}
				onCancel={() => handleCancel()}
				onSubmit={(formValues) => handleSubmit(formValues)}
				onDelete={() => handleDelete()}
				values={selectedInstruction}
				formItems={[
					{
						id: 'name',
						label: 'Namn',
						decorator: {
							rules: [{ required: true, message: 'Namn är obligatoriskt' }],
						},
						component: <Input />,
					},
					{
						id: 'type',
						label: 'Typ',
						decorator: {
							rules: [{ required: true, message: 'Typ är obligatoriskt' }],
							initialValue: 'SUMMARY',
						},
						component: (
							<Select>
								<Option value='SUMMARY'>Summering</Option>
							</Select>
						),
					},
					{
						id: 'instruction',
						label: 'Instruktion',
						decorator: {
							rules: [{ required: true, message: 'Instruktion är obligatoriskt' }],
						},
						component: <Input.TextArea autosize />,
					},
					{
						id: 'enabled',
						label: 'Aktiv',
						decorator: {
							valuePropName: 'checked',
							initialValue: true,
						},
						component: <Switch />,
					},
				]}
			/>
		</div>
	);
}

AiConfigInstructions.propTypes = {
	showCreateNew: PropTypes.bool,
};

AiConfigInstructions.defaultProps = {
	showCreateNew: false,
};

export { AiConfigInstructions, AiConfigInstructions as default };



// WEBPACK FOOTER //
// ./src/components/ai/config/Instructions.jsx